import { Fragment } from 'react';

import BlockMedia from 'components/BlockMedia';
import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import type { DigizuiteAsset } from 'models/asset';
import type { ColorField, Field, SitecoreBlockProps } from 'models/sitecore';
import type { JSONObject } from 'types';
import { getEditorMargin } from 'utils/business-logic';
import { getAsset, is } from 'utils/helpers';

interface Item {
	body?: JSONObject;
	campaignBubbleLabel?: Field<string>;
	campaignBubbleSale?: Field<boolean>;
	campaignBubbleText?: Field<string>;
	landscapeImage: Field<DigizuiteAsset[]>;
	portraitImage: Field<DigizuiteAsset[]>;
	portraitVideo?: Field<DigizuiteAsset[]>;
	preventAutoplay?: Field<boolean>;
	video?: Field<DigizuiteAsset[]>;
}

interface Props extends SitecoreBlockProps {
	fields?: {
		backgroundColor?: ColorField;
		datasourceName?: string;
		items?: Item[];
	};
}

export default function MediaDoubleWide({ fields, params }: Props) {
	if (!fields) {
		return null;
	}

	const items = fields.items
		?.map((item) => ({
			campaignBubbleLabel: item.campaignBubbleLabel?.value,
			campaignBubbleText: item.campaignBubbleText?.value,
			campaignBubbleSale: Boolean(item.campaignBubbleSale?.value),
			landscapeImage: getAsset('Templated 2:1', item.landscapeImage),
			landscapeVideo: getAsset('Source Copy', item.video),
			portraitImage: getAsset('Templated 3:4', item.portraitImage),
			portraitVideo: getAsset('Source Copy', item.portraitVideo),
			preventAutoplay: Boolean(item.preventAutoplay?.value),
			productsRequestBody: item.body,
		}))
		.filter(
			(item) =>
				(item.portraitImage ?? item.portraitVideo) &&
				(item.landscapeImage ?? item.landscapeVideo),
		);

	if (!is.arrayWithLength(items) || items.length < 2) {
		return (
			<ComponentPlaceholder
				componentName="MediaDoubleWide"
				description="Requires two items with content (image or video) for both mobile and desktop"
				className={getEditorMargin(params)}
			/>
		);
	}

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
			withGrid
			backgroundColor={fields.backgroundColor?.fields?.colorHex?.value}
		>
			{items.map(
				({
					campaignBubbleLabel,
					campaignBubbleSale,
					campaignBubbleText,
					landscapeImage,
					landscapeVideo,
					portraitImage,
					portraitVideo,
					preventAutoplay,
					productsRequestBody,
				}) => (
					<Fragment key={`${landscapeImage?.src}-${landscapeVideo?.src}`}>
						<BlockMedia
							image={[
								{
									src: portraitImage?.src,
									alt: portraitImage?.alt,
									variant: 'portrait',
									className: 'md:hidden',
								},
								{
									src: landscapeImage?.src,
									alt: landscapeImage?.alt,
									variant: 'landscape2x1Large',
									className: 'max-md:hidden',
								},
							]}
							video={[
								portraitVideo && {
									...portraitVideo,
									className: 'md:hidden',
								},
								landscapeVideo && {
									...landscapeVideo,
									className: 'max-md:hidden',
								},
							].filter(is.truthy)}
							className="col-span-2 md:col-span-6"
							campaignBubbleLabel={campaignBubbleLabel}
							campaignBubbleSale={campaignBubbleSale}
							campaignBubbleText={campaignBubbleText}
							campaignBubbleSmallSize="small"
							campaignBubbleLargeSize="medium"
							preventAutoplay={preventAutoplay}
							productsRequestBody={productsRequestBody}
							productsDisplayType="shopByMedia"
							gtmItemListId="block"
							gtmItemListName={`Block: ${fields.datasourceName ?? 'MediaDoubleWideUnknown'}`}
						/>
					</Fragment>
				),
			)}
		</LayoutContainer>
	);
}
MediaDoubleWide.displayName = 'MediaDoubleWide';
